import React, { useContext } from 'react';
import { ProductModelProps, ProductVariation } from '@typings/product';
import noop from 'lodash/noop';
import Product from '@models/product';
import useAddToBasket from '@hooks/basket/useAddToBasket';
import useShopDataProducts from '@hooks/product/useShopDataProducts';

interface ContextProps {
  totalPrice: string;
  originalPrice: string;
  addToCart: (quantity: number) => void;
  addVariationToCart: (variation: ProductVariation, quantity: number) => void;
  isLoading: boolean;
  isLoadingAddToCart: boolean;
  mainProduct: Product | null;
  productList: Array<Product>;
}

export const ProductContext = React.createContext<ContextProps>({
  totalPrice: '',
  originalPrice: '',
  addToCart: noop,
  addVariationToCart: noop,
  isLoading: false,
  isLoadingAddToCart: false,
  mainProduct: null,
  productList: [],
});

interface Props {
  children: React.ReactNode;
  product: Product;
  altProduct?: ProductModelProps;
  addOns?: Array<any>; // Kept for backward compatibility but not used
  locale: string;
}

export const ProductProvider = ({ children, product }: Props) => {
  const { data: shopDataProducts, isLoading: isShopProductListLoading } = useShopDataProducts(
    [product.sku].filter(Boolean),
    true,
  );

  const { trigger, isMutating: isLoadingAddToCart } = useAddToBasket();

  const mainProduct = React.useMemo(() => {
    const shopData = shopDataProducts?.find((shop) => shop?.sku === product?.sku);
    if (shopData && product) {
      const clone = Object.create(
        Object.getPrototypeOf(product),
        Object.getOwnPropertyDescriptors(product),
      );
      clone.setShop(shopData);
      return clone;
    }
    return product;
  }, [product, shopDataProducts]);

  const addToCart = React.useCallback(
    (quantity = 1) => {
      if (mainProduct && quantity >= 1) {
        // Clone the product to avoid mutating the cached instance
        const productCopy = Object.create(
          Object.getPrototypeOf(mainProduct),
          Object.getOwnPropertyDescriptors(mainProduct),
        );
        productCopy.quantity = quantity;
        trigger([productCopy]);
      }
    },
    [mainProduct, trigger],
  );

  const addVariationToCart = React.useCallback(
    (variation: ProductVariation, quantity = 1) => {
      const maxStock = Math.max(0, Math.min(variation.inventory?.stock_level ?? 99, 99));
      if (variation.isOrderable && quantity >= 1 && quantity <= maxStock) {
        trigger([{ ...variation, quantity }]);
      }
    },
    [trigger],
  );

  const { totalPrice, originalPrice } = React.useMemo(
    () => ({
      totalPrice: mainProduct?.price?.toFixed(2) ?? '0.00',
      originalPrice: mainProduct?.originalPrice?.toFixed(2) ?? '0.00',
    }),
    [mainProduct],
  );

  return (
    <ProductContext.Provider
      value={{
        addToCart,
        addVariationToCart,
        isLoading: isShopProductListLoading,
        totalPrice,
        originalPrice,
        isLoadingAddToCart,
        mainProduct,
        productList: [], // Empty array - addon functionality removed
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProductContext = () => useContext(ProductContext);
